/**
 * `<SigningKeysList>` — orchestrates the JWT Signing Keys tab body.
 * Renders the current key, previous keys, and the rotate / retire flows.
 *
 * Per FD § 2.4. Mutations are pessimistic (NFR-Design § 1): rotate and
 * retire await server success before updating the visible state.
 *
 * Server-side `PermissionGuard` (`signing-key:manage`) is canonical for
 * security — this client is only mounted when `useIsAdmin()` is true (the
 * tab is hidden otherwise). NFR-SEC-14 defense-in-depth.
 */

import { useWorkspace } from "@cometchat-ai/auth/client";
import {
  Alert,
  AlertDescription,
  Button,
  Skeleton,
  TableBody,
  TableHeader,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, RotateCw } from "../icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid, DataGridCell, DataGridHead, DataGridHeaderRow, DataGridRow } from "../data-grid";
import { ApiError } from "../../lib/api/errors";
import { useContactsClient } from "../../lib/api/use-contacts-client";
import type { SigningKey, SigningKeyAlgorithm } from "../../lib/api/types";
import { relativeTime } from "../../lib/relative-time";

import { RetireSigningKeyDialog } from "./RetireSigningKeyDialog";
import { RotateSigningKeyDialog } from "./RotateSigningKeyDialog";
import { SigningKeyPemDisplay } from "./SigningKeyPemDisplay";

const QUERY_KEY = ["signing-keys", "list"] as const;

function pickCurrent(keys: SigningKey[]): SigningKey | undefined {
  return keys.find((k) => k.status === "current");
}

function pickPrevious(keys: SigningKey[]): SigningKey[] {
  return keys.filter((k) => k.status === "previous");
}

export function SigningKeysList() {
  const { t } = useTranslation("signing-keys");
  const { workspace } = useWorkspace();
  const client = useContactsClient();
  const qc = useQueryClient();

  const [rotateOpen, setRotateOpen] = useState(false);
  const [retireKid, setRetireKid] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => client.listSigningKeys(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const rotateMutation = useMutation({
    mutationFn: (algorithm: SigningKeyAlgorithm) => client.rotateSigningKey(algorithm),
    onSuccess: () => {
      toast.success(t("rotate.toast"));
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      setRotateOpen(false);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError && err.message ? err.message : t("rotate.error");
      toast.error(msg);
    },
  });

  const retireMutation = useMutation({
    mutationFn: (kid: string) => client.retireSigningKey(kid),
    onSuccess: () => {
      toast.success(t("retire.toast"));
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      setRetireKid(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError && err.message ? err.message : t("retire.error");
      toast.error(msg);
    },
  });

  if (listQuery.isPending) {
    return (
      <div className="space-y-3" data-testid="signing-keys-skeleton">
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-12 w-2/3 rounded-lg" />
      </div>
    );
  }

  if (listQuery.error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t("load.error")}</AlertDescription>
      </Alert>
    );
  }

  const keys = listQuery.data ?? [];
  const current = pickCurrent(keys);
  const previous = pickPrevious(keys);

  if (!current) {
    return (
      <>
        <div
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-12 text-center"
          data-testid="signing-keys-empty"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <KeyRound className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">{t("empty.title")}</p>
            <p className="mx-auto max-w-sm text-[13px] text-muted-foreground">{t("empty.body")}</p>
          </div>
          <Button
            size="sm"
            onClick={() => setRotateOpen(true)}
            className="h-8 gap-1.5 rounded-md text-[13px]"
            data-testid="signing-keys-generate"
          >
            <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
            {t("empty.cta")}
          </Button>
        </div>
        <RotateSigningKeyDialog
          open={rotateOpen}
          onOpenChange={setRotateOpen}
          workspaceSlug={workspace.slug}
          busy={rotateMutation.isPending}
          isGenerate
          onConfirm={(alg) => rotateMutation.mutate(alg)}
        />
      </>
    );
  }

  return (
    <div className="space-y-5" data-testid="signing-keys-list">
      <SigningKeyPemDisplay
        signingKey={current}
        rotatedLabel={
          current.rotatedAt
            ? t("current.rotatedAgo", { relative: relativeTime(current.rotatedAt) })
            : undefined
        }
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setRotateOpen(true)}
            className="h-8 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
            data-testid="signing-keys-rotate-trigger"
          >
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
            {t("current.rotate")}
          </Button>
        }
      />

      <section>
        <h3 className="mb-2.5 text-sm font-semibold tracking-[0.01em] text-foreground">
          {t("previous.heading")}
        </h3>
        {previous.length === 0 ? (
          <p
            className="rounded-lg border border-border px-3 py-2.5 text-[13px] text-muted-foreground"
            data-testid="signing-keys-previous-empty"
          >
            {t("previous.empty")}
          </p>
        ) : (
          <div
            className="overflow-hidden rounded-xl border border-border"
            data-testid="signing-keys-previous-list"
          >
            <DataGrid>
              <TableHeader>
                <DataGridHeaderRow>
                  <DataGridHead className="px-3">{t("previous.kidColumn")}</DataGridHead>
                  <DataGridHead className="w-40 px-3">{t("previous.rotatedColumn")}</DataGridHead>
                  <DataGridHead className="w-28 px-3 text-right" divider={false} />
                </DataGridHeaderRow>
              </TableHeader>
              <TableBody>
                {previous.map((k) => (
                  <DataGridRow key={k.kid} className="last:border-b-0">
                    <DataGridCell className="px-0">
                      <code className="rounded-[5px] bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-1.5 py-0.5 font-mono text-xs text-foreground">
                        {k.kid}
                      </code>
                    </DataGridCell>
                    <DataGridCell className="whitespace-nowrap px-0">
                      {relativeTime(k.rotatedAt)}
                    </DataGridCell>
                    <DataGridCell className="px-0 text-right" divider={false}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRetireKid(k.kid)}
                        className="h-7 rounded-md text-[13px] text-destructive hover:bg-[color:color-mix(in_oklch,var(--color-danger)_10%,transparent)] hover:text-destructive"
                        data-testid={`signing-keys-retire-trigger-${k.kid}`}
                      >
                        {t("previous.retire")}
                      </Button>
                    </DataGridCell>
                  </DataGridRow>
                ))}
              </TableBody>
            </DataGrid>
          </div>
        )}
      </section>

      <RotateSigningKeyDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        workspaceSlug={workspace.slug}
        busy={rotateMutation.isPending}
        onConfirm={(alg) => rotateMutation.mutate(alg)}
      />
      <RetireSigningKeyDialog
        open={retireKid !== null}
        onOpenChange={(open) => {
          if (!open) setRetireKid(null);
        }}
        kid={retireKid ?? ""}
        busy={retireMutation.isPending}
        onConfirm={() => {
          if (retireKid) retireMutation.mutate(retireKid);
        }}
      />
    </div>
  );
}
